// Which leave actions the signed-in user can take. Mirrors the backend rules
// in BE-ESS ess/leave/workflow.py:
// - the approver whose turn it is decides; the HR leave approver can decide
//   at any level without waiting (their decision is final);
// - the employee withdraws a pending leave at once, or asks to cancel an
//   approved one (that needs approval);
// - HR can cancel any active leave outright, including old leaves stuck in
//   "cancel_rejected".

const REQUEST_OPEN = ["pending", "escalated"];
const HR_CANCELLABLE = [...REQUEST_OPEN, "approved", "cancel_rejected"];
const OWNER_CANCELLABLE = [...REQUEST_OPEN, "approved"];

export const isLeaveApprover = (user) =>
  [true, "True", "true"].includes(user?.is_leave_request_approver);

// Ids can arrive as numbers or strings; compare them as strings.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const roles = (user, leave) => {
  const isOwner = sameId(user?.id, leave?.company_user?.id);
  return {
    isOwner,
    isCurrent: sameId(user?.id, leave?.current_approver?.id),
    isHr: !isOwner && isLeaveApprover(user),
  };
};

/** Status values the user may submit for this leave, e.g. ["approved", "rejected"]. */
export const getLeaveActions = (user, leave) => {
  if (!user || !leave) return [];
  const status = leave.approval_status;
  const { isOwner, isCurrent, isHr } = roles(user, leave);
  const canDecide = isCurrent || isHr;
  const actions = [];
  if (canDecide && REQUEST_OPEN.includes(status)) actions.push("approved", "rejected");
  if (canDecide && status === "cancel_pending") actions.push("cancel_approved", "cancel_rejected");
  const canCancel =
    (isOwner && OWNER_CANCELLABLE.includes(status)) ||
    (isHr && HR_CANCELLABLE.includes(status));
  if (canCancel) actions.push("cancelled");
  return actions;
};

/**
 * What "cancel" means for this user: "hr" (cancelled immediately),
 * "withdraw" (own pending leave, cancelled immediately) or "request"
 * (own approved leave, needs approval).
 */
export const getCancelMode = (user, leave) => {
  const { isOwner, isHr } = roles(user, leave);
  if (isHr) return "hr";
  if (isOwner && REQUEST_OPEN.includes(leave?.approval_status)) return "withdraw";
  return "request";
};

/** True when HR is deciding ahead of the approver whose turn it is. */
export const isHrOverride = (user, leave) => {
  const { isCurrent, isHr } = roles(user, leave);
  return isHr && !isCurrent;
};

export const CANCEL_COPY = {
  withdraw: {
    title: "Withdraw this leave request?",
    sub: "No one has approved it yet, so it is cancelled straight away and you can apply again.",
    confirmSub: "The request is cancelled immediately. You can apply again for these dates.",
    button: "Withdraw Request",
    confirm: "Withdraw Request",
  },
  request: {
    title: "Need to cancel this leave?",
    sub: "Your manager will be notified to approve the cancellation.",
    confirmSub: "A cancellation request will be sent to your manager for approval.",
    button: "Request Cancellation",
    confirm: "Confirm Cancellation",
  },
  hr: {
    title: "Cancel this leave?",
    sub: "As the HR approver, your cancellation takes effect immediately. The employee can then apply again.",
    confirmSub: "The leave is cancelled now (and in Business Central if it was already sent there).",
    button: "Cancel Leave",
    confirm: "Confirm Cancellation",
  },
};
